/**
 * Pick the Hand Role — sheet 3's framework as a multiple-choice quiz.
 *
 * 5 roles in sheet 3 (Value, Bluff w/ FE, Semi-bluff, Protection,
 * Give up). For grading clarity we collapse Protection into Value
 * — most "bet for protection" cases are also "bet for value" (the
 * hand is ahead now, betting denies equity), so the answer in a 4-MC
 * is "Value" either way. The 4 surfaced roles:
 *
 *   - **Value** — bet because worse hands call (TPTK, overpair, etc.)
 *   - **Bluff** — bet because better folds (no equity, range advantage)
 *   - **Semi-bluff** — bet because better folds AND we have equity
 *   - **Give up** — none of the above; check
 *
 * Spots are hand-authored: BTN raiser, BB caller, single-raised pot.
 * Each spot's "correct" role is the one sheet 3's framework points at.
 */

import { pickFrom } from "@/features/quiz/lib/common";

export type HandRole = "value" | "bluff" | "semi_bluff" | "give_up";

export const ROLE_LABEL: Record<HandRole, string> = {
  value: "Value",
  bluff: "Bluff (FE)",
  semi_bluff: "Semi-bluff",
  give_up: "Give up",
};

export interface HandRoleSpot {
  /** Hero's hole cards, written for display ("AhKh", "65s"). */
  hand: string;
  /** Flop, e.g. "K72r" or "JhTh2c". */
  board: string;
  /** Hero context. Always BTN raiser, BB caller in v1 for consistency. */
  context: string;
  role: HandRole;
  rationale: string;
}

export const HAND_ROLE_BANK: HandRoleSpot[] = [
  // VALUE ------------------------------------------------------------
  {
    hand: "AKo",
    board: "K72r",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "value",
    rationale: "TPTK on a dry board. Worse calls (KQ/KJ/KT, 7x, 22-66 set-mining), so it's a clear value bet.",
  },
  {
    hand: "QQ",
    board: "J73r",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "value",
    rationale: "Overpair on a dry board. Gets called by Jx, mid pairs, draws on backdoor connections. Clean value.",
  },
  {
    hand: "JJ",
    board: "332r",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "value",
    rationale: "Overpair on a paired low board. Worse calls (small pairs, 3x set-mining lines, weak Ax bluff-catching). Value bet.",
  },
  {
    hand: "AcKc",
    board: "KhQh4d",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "value",
    rationale: "TPTK. Worse calls (KJ/KT, QJ, QT, second pair). Bet for value before more draws complete.",
  },
  {
    hand: "99",
    board: "765r",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "value",
    rationale: "Overpair on a wet board. Vulnerable enough that betting denies equity AND gets called by worse pairs / draws, making it value-with-protection.",
  },

  // BLUFF (with fold equity) -----------------------------------------
  {
    hand: "65s",
    board: "AKQr",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "bluff",
    rationale: "Zero equity, but BB has very few combos that continue (mostly AQ/KQ, which fold to a two-barrel anyway). Range advantage to BTN; betting prints fold equity.",
  },
  {
    hand: "K9s",
    board: "A6r3d",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "bluff",
    rationale: "No equity but BB rarely has an A here (raises preflop most Ax). K blocker improves the bluff. Range advantage + low fold-resistance.",
  },
  {
    hand: "T8s",
    board: "AK2r",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "bluff",
    rationale: "No equity, but BTN's range crushes A-high dry. BB has only Ax/Kx to continue with, folding everything else. Clean bluff combo.",
  },

  // SEMI-BLUFF -------------------------------------------------------
  {
    hand: "JhTh",
    board: "Kh7s2c",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "semi_bluff",
    rationale: "Flush draw + two overcards. Folds out better (BB's air, weak Kx) AND has 9 hearts + 6 overcard outs if called: textbook semi-bluff.",
  },
  {
    hand: "AhQh",
    board: "Jh8h2c",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "semi_bluff",
    rationale: "Nut flush draw + overcard pair outs. Folds out a lot of BB's range, and even when called we have 9 + 3 outs to improve.",
  },
  {
    hand: "AcKc",
    board: "Jc8c4d",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "semi_bluff",
    rationale: "Nut flush draw + two overcards. Tons of equity when called, AND folds out a lot of BB's range that doesn't connect. Semi-bluff supreme.",
  },
  {
    hand: "65s",
    board: "432r",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "semi_bluff",
    rationale: "Open-ended straight draw with backdoor flush. Range advantage sits with BB on low boards, but the equity-when-called makes this a semi-bluff, not a pure bluff or give-up.",
  },
  {
    hand: "QcJc",
    board: "Tc9d4s",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "semi_bluff",
    rationale: "Open-ender + two overcards + backdoor flush. Folds out 7x, weak Tx, low pairs. Strong equity if called.",
  },

  // GIVE UP ----------------------------------------------------------
  {
    hand: "T9o",
    board: "A72r",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "give_up",
    rationale: "No equity, no usable blockers, no overcards. Betting folds out hands that beat you anyway (none) and gets called by hands you can't outdraw. Check.",
  },
  {
    hand: "22",
    board: "AKQss",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "give_up",
    rationale: "Underpair on a board with multiple overcards + flush draw possibilities. 2 outs to a set, no fold equity (BB doesn't fold Ax/Kx/Qx). Check.",
  },
  {
    hand: "33",
    board: "AKQr",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "give_up",
    rationale: "Underpair, 2 outs, no fold equity vs BB's continuing range. Betting is a money-burning play.",
  },
  {
    hand: "76o",
    board: "AK2r",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "give_up",
    rationale: "No equity, no real fold-equity story (offsuit nothing). Check and hope to realize at showdown if anything.",
  },
  {
    hand: "54o",
    board: "AKQss",
    context: "BTN raiser, BB caller: flop c-bet decision",
    role: "give_up",
    rationale: "Total air on a connected high board where BB connects with plenty. Even backdoor potential is weak. Check fold.",
  },
];

export function generateQuestion(rand: () => number = Math.random): HandRoleSpot {
  return pickFrom(HAND_ROLE_BANK, rand);
}

export const _internals = { HAND_ROLE_BANK };
